import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type ComputedArrivalData } from "@src/appV2/redesign/ArrivalTime/computeArrivalData";
import { formatArrivalTime } from "@src/appV2/redesign/ArrivalTime/formatArrivalTime";
import { useGetCachedShiftRouteEstimate } from "@src/appV2/redesign/ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { ConfirmBookingBottomSheet } from "@src/appV2/redesign/Shift/BookingConfirmationBottomSheet/ConfirmBookingBottomSheet";
import { useMinuteCountdown } from "@src/appV2/redesign/Shift/BookingConfirmationBottomSheet/useMinuteCountdown";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { useMemo } from "react";

import { useWorkplaceBadge } from "../../../Workplace/hooks/useWorkplaceBadge";
import { useDistanceToWorkplace } from "../../../Workplace/useDistanceToWorkplace";
import { getShiftTimingText } from "../../ShiftDetails/utils/getShiftTimingText";
import { type ShiftInvite } from "../types";

export interface ShiftInviteConfirmShiftBookingBottomSheetContainerProps {
  modalState: UseModalState;
  shiftInvite: ShiftInvite;
  isConfirming: boolean;
  onConfirm: (computedArrivalData: ComputedArrivalData) => Promise<void>;
}

export function ShiftInviteConfirmShiftBookingBottomSheetContainer(
  props: ShiftInviteConfirmShiftBookingBottomSheetContainerProps
) {
  const { modalState, shiftInvite, onConfirm, isConfirming } = props;

  const { id: shiftInviteId, attributes } = shiftInvite;
  const { workplaceId, shiftDetails, facility: workplace } = attributes;
  const { start: shiftStartIso, end: shiftEndIso, pay } = shiftDetails;
  const shiftStart = parseISO(shiftStartIso);
  const shiftEnd = parseISO(shiftEndIso);

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const minutesUntilShiftStart = useMinuteCountdown({ date: shiftStart });

  const workplaceGeoLocation = {
    longitude: workplace.geoLocation.coordinates[0],
    latitude: workplace.geoLocation.coordinates[1],
  };

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation,
  });

  const { isEnabled: isIndependentContractorBadgesRequired } = useWorkplaceBadge(workplaceId);

  // This should be present from the shift invite page
  const computedArrivalData = useGetCachedShiftRouteEstimate(shiftInviteId);

  const targetArrivalTime = computedArrivalData?.targetArrivalTime;

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_CONFIRM_BOOKING_SHEET_VIEWED,
    { shiftInviteId },
    { enabled: modalState.modalIsOpen }
  );

  const isShiftWithin24Hours = minutesUntilShiftStart <= 24 * 60;

  const urgentPillLabel = useMemo(() => {
    if (isDefined(targetArrivalTime)) {
      return `Please arrive by ${formatArrivalTime(
        utcToZonedTime(targetArrivalTime, workplace.tmz ?? "America/Los_Angeles")
      )}`;
    }

    if (minutesUntilShiftStart <= 0) {
      return "Please arrive as soon as possible";
    }

    if (isShiftWithin24Hours) {
      return getShiftTimingText({
        currentDate: new Date(),
        shiftStartDate: shiftStart,
      });
    }

    return undefined;
  }, [isShiftWithin24Hours, shiftStart, targetArrivalTime, workplace.tmz, minutesUntilShiftStart]);

  return (
    <ConfirmBookingBottomSheet
      modalState={modalState}
      shiftStart={shiftStart}
      shiftEnd={shiftEnd}
      timezone={workplace.tmz ?? "America/New_York"}
      mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={workplace.requiresLunchBreak ?? true}
      urgentPillLabel={urgentPillLabel}
      workplaceName={workplace.name}
      formattedDistance={formattedDistance}
      hourlyPay={formatDollarsAsUsd(pay)}
      isLoading={isConfirming}
      totalWithBoostPay={undefined}
      /* Ideally we would have the following fields below defined properly but we don't have them in the shift invite object */
      useShiftBoost={false}
      shiftBoostAmount={undefined}
      isInstantPay={false}
      isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
      /*****/
      onConfirmBooking={async () => {
        if (!isDefined(computedArrivalData)) {
          return;
        }

        await onConfirm(computedArrivalData);
      }}
    />
  );
}
